import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { saveAccountantPartnership } from "../lib/accountantPartnership";
import { ArrowLeft, LogOut, TicketPercent } from "lucide-react";

const AccountantPartnerLandingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadLink = async () => {
      try {
        const response = await axios.get(`${API}/accountants/links/${token}/public`);
        const data = response.data;
        setLinkData(data);
        saveAccountantPartnership({
          token,
          discount_percent: data.discount_percent || 0,
          coupon_code: data.coupon_code || null,
          accountant_name: data.accountant?.name || null,
          accountant_company_name: data.accountant?.company_name || null,
          client_name: data.client_name || null,
          client_email: data.client_email || null,
          captured_at: new Date().toISOString(),
        });
      } catch (err) {
        setError(err.response?.data?.detail || "Este link de parceria não está disponível.");
      } finally {
        setLoading(false);
      }
    };
    loadLink();
  }, [token]);

  useEffect(() => {
    if (!loading && user && !error) {
      if (user.role === "client") navigate("/dashboard", { replace: true });
    }
  }, [loading, user, error, navigate]);

  const continueAsClient = async (destination) => {
    if (user?.role === "accountant") {
      await logout();
    }
    navigate(destination);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-navy mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao início
        </Link>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center">
            <BrandLogo size="lg" className="mx-auto mb-4" />
            <CardTitle className="font-serif text-3xl text-navy">Parceria com Contador</CardTitle>
            <CardDescription>
              Use este acesso para realizar o diagnóstico empresarial com acompanhamento do parceiro contador.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
            ) : (
              <>
                {user?.role === "accountant" && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    Este navegador está logado como contador parceiro. Para seguir a jornada do cliente por este link,
                    saia da conta atual e continue como cliente.
                  </div>
                )}

                <div className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Benefício atual</p>
                      <p className="mt-1 text-xl font-semibold text-navy">
                        {Number(linkData?.discount_percent || 0) > 0
                          ? `${linkData.discount_percent}% de desconto`
                          : "Link sem desconto, com vínculo de parceria"}
                      </p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-800">
                      <TicketPercent className="h-4 w-4 mr-1" />
                      Parceria ativa
                    </Badge>
                  </div>
                  <div className="mt-4 space-y-1 text-sm text-slate-600">
                    <p>Contador parceiro: {linkData?.accountant?.name || "N/A"}</p>
                    <p>Empresa/Escritório: {linkData?.accountant?.company_name || "Não informado"}</p>
                    {linkData?.client_email && <p>E-mail previsto: {linkData.client_email}</p>}
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <Button className="bg-navy hover:bg-navy/90" onClick={() => continueAsClient("/register")}>
                    Criar conta e iniciar
                  </Button>
                  <Button variant="outline" onClick={() => continueAsClient("/login")}>
                    Já tenho conta
                  </Button>
                </div>

                {user?.role === "accountant" && (
                  <Button variant="ghost" className="w-full" onClick={() => continueAsClient("/register")}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair da conta do contador e continuar
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantPartnerLandingPage;
